import type { Batch, Answer, SigtestValue } from '@aytm/app/stats/page/question-views/cq/components/partial/types'

/**
 * Converts a 1-indexed position to a letter (A, B, C, ... Z, AA, AB, etc.)
 */
export function getSigtestLetterFromPosition(position: number): string {
  let letter = ''
  let a = 1
  let b = 26
  let pos = position - a

  while (0 <= pos) {
    letter = String.fromCharCode(Math.floor((pos % b) / a) + 65) + letter
    a = b
    b *= 26
    pos -= a
  }

  return letter
}

/**
 * Gets sigtest letters for all batches
 */
export function getSigtestLetters(batches: Batch[]): string[] {
  return batches.map((_, index) => getSigtestLetterFromPosition(index + 1))
}

/**
 * Gets sigtest stats for all batches and answer variants
 */
export function getSigtestStats(
  batches: Batch[],
  answer: Answer,
  sigtestLetters: string[],
  defaultConfidenceLevel: number,
  defaultSignificanceLevel: number
): SigtestValue[][][] {
  const confidenceLevel = answer.confidenceLevel ?? defaultConfidenceLevel ?? 0.95
  const significanceLevel = answer.significanceLevel ?? defaultSignificanceLevel ?? 0.9

  const levelKey = `${1000 * significanceLevel}_${1000 * confidenceLevel}`

  return batches.map(b => {
    const batchStats = (answer[`b${b.position}`] as Record<string, unknown>) || {}

    return (b.answerVariants || []).map(av => {
      const avStats = (batchStats[`a${av.position}`] as Record<string, unknown>) || {}
      const sigtestData = (avStats.sigtest as Record<string, Array<Record<string, unknown>>>) || {}
      const sigtestArray = sigtestData[levelKey] || []

      const data: SigtestValue[] = sigtestArray
        .map(sigtest => {
          const sigtestDir = sigtest.dir !== undefined ? sigtest.dir : sigtest.d
          const sigtestIndex = (sigtest.index !== undefined ? sigtest.index : sigtest.i) as number
          const sigtestLevel = (sigtest.level !== undefined ? sigtest.level : sigtest.l) as number
          const sigtestLetter = sigtestLetters[sigtestIndex]

          if (sigtestLetter !== undefined && sigtestLevel !== undefined) {
            const letter =
              sigtestLevel === 1
                ? sigtestLetter.toLowerCase()
                : sigtestLevel === 2
                  ? sigtestLetter.toUpperCase()
                  : sigtestLetter

            return {
              letter,
              level: sigtestLevel,
              dir: sigtestDir as number | undefined
            } as SigtestValue
          }
          return null
        })
        .filter((s): s is SigtestValue => null !== s)

      data.sort((a, b) => parseInt(a.letter, 36) - parseInt(b.letter, 36))

      return data
    })
  })
}

/**
 * Gets crosstab stats for all batches and answer variants
 */
export function getCrosstabStats(
  batches: Batch[],
  answer: Answer,
  defaultConfidenceLevel: number
): (string | null)[][] {
  const confidenceLevel = answer.confidenceLevel ?? defaultConfidenceLevel ?? 0.95
  const levelKey = `${1000 * confidenceLevel}`

  // Result is indexed by batch index downstream, so batches are never elided (the per-batch array is always truthy)
  return batches.map(b => {
    const batchStats = (answer[`b${b.position}`] as Record<string, unknown>) || {}

    return (b.answerVariants || []).map(av => {
      const avStats = (batchStats[`a${av.position}`] as Record<string, unknown>) || {}
      const crosstabData = (avStats.crosstab as Record<string, string | null>) || {}
      return crosstabData[levelKey] ?? null
    })
  })
}
